import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfirm } from '../context/ConfirmContext';
import './BrandManager.css';

const BrandManager = ({ onBrandAdded, isVisible, onToggle }) => {
    const confirm = useConfirm();
    const [brands, setBrands] = useState([]);
    const [newBrandName, setNewBrandName] = useState('');
    const [newBrandLogo, setNewBrandLogo] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // State for the edit modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [updatedBrandName, setUpdatedBrandName] = useState('');
    const [updatedBrandLogo, setUpdatedBrandLogo] = useState(null);
    const [removeLogo, setRemoveLogo] = useState(false);


    const fetchBrands = async () => {
        try {
            const response = await api.get('/brands');
            setBrands(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            setError('Failed to fetch brands.');
            setBrands([]);
        }
    };

    useEffect(() => {
        if (isVisible && !hasFetched) {
            fetchBrands();
            setHasFetched(true);
        }
    }, [isVisible, hasFetched]);

    const handleAddBrand = async (e) => {
        e.preventDefault();
        if (!newBrandName.trim()) {
            setError('Brand name cannot be empty.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('name', newBrandName);
            if (newBrandLogo) {
                formData.append('logo', newBrandLogo);
            }

            await api.post('/brands', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setNewBrandName('');
            setNewBrandLogo(null);
            document.getElementById('newBrandLogoInput').value = '';
            await fetchBrands();
            if (onBrandAdded) onBrandAdded();
        } catch (err) {
            if (err.response && err.response.status === 409) {
                setError('This brand already exists.');
            } else {
                setError('Failed to add brand.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setNewBrandLogo(e.target.files[0]);
    };

    const handleEditFileChange = (e) => {
        setUpdatedBrandLogo(e.target.files[0]);
        setRemoveLogo(false); // If a new file is chosen, we are not removing the logo
    }

    const openEditModal = (brand) => {
        setEditingBrand(brand);
        setUpdatedBrandName(brand.name);
        setUpdatedBrandLogo(null);
        setRemoveLogo(false);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingBrand(null);
        setError('');
    };

    const handleUpdateBrand = async (e) => {
        e.preventDefault();
        if (!editingBrand || !updatedBrandName.trim()) {
            setError('Brand name cannot be empty.');
            return;
        }
        setIsLoading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('name', updatedBrandName);
            if (updatedBrandLogo) {
                formData.append('logo', updatedBrandLogo);
            }
            formData.append('removeLogo', removeLogo);


            await api.put(`/brands/${editingBrand.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            closeEditModal();
            await fetchBrands();
            if (onBrandAdded) onBrandAdded();
        } catch (err) {
            setError('Failed to update brand.');
        } finally {
            setIsLoading(false);
        }
    };


    const handleDeleteBrand = async (brandId) => {
        const ok = await confirm({
            title: 'Delete this brand?',
            message: 'Brands that still have products attached cannot be deleted.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;

        try {
            await api.delete(`/brands/${brandId}`);
            await fetchBrands();
            if (onBrandAdded) onBrandAdded();
        } catch (err) {
            setError('Failed to delete brand. Make sure no products are associated with it.');
        }
    };


    return (
        <div className={`collapsible-widget ${isVisible ? 'is-expanded' : ''}`}>
            <div className="widget-header" onClick={onToggle}>
                <h3>Manage Brands</h3>
                <span className="toggle-icon">+</span>
            </div>
            <div className="widget-content">
                <div className="brand-manager-content">
                    <form onSubmit={handleAddBrand} className="brand-add-form">
                        <input
                            type="text"
                            value={newBrandName}
                            onChange={(e) => setNewBrandName(e.target.value)}
                            placeholder="Enter new brand name"
                            className="brand-input"
                        />
                        <input
                            id="newBrandLogoInput"
                            type="file"
                            onChange={handleFileChange}
                            className="brand-input"
                        />
                        <button type="submit" className="add-brand-btn" disabled={isLoading}>
                            {isLoading ? 'Adding...' : '+ Add Brand'}
                        </button>
                    </form>
                    {error && <p className="brand-error">{error}</p>}
                    <div className="brand-list">
                        {brands && brands.length > 0 ? (
                            brands.map(brand => (
                                <div key={brand.id} className="brand-tag-container">
                                    <span className="brand-tag">
                                        {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="brand-logo-preview" />}
                                        {brand.name}
                                    </span>
                                    <div className="brand-actions">
                                        <button onClick={() => openEditModal(brand)} className="brand-action-btn edit-btn">Edit</button>
                                        <button onClick={() => handleDeleteBrand(brand.id)} className="brand-action-btn delete-btn">Delete</button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p>No brands created yet.</p>
                        )}
                    </div>
                </div>
            </div>

            {isEditModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Edit Brand</h2>
                        <form onSubmit={handleUpdateBrand}>
                            <div className="form-group">
                                <label>Brand Name</label>
                                <input
                                    type="text"
                                    value={updatedBrandName}
                                    onChange={(e) => setUpdatedBrandName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Brand Logo</label>
                                {editingBrand.logoUrl && !removeLogo && <img src={editingBrand.logoUrl} alt="Current logo" className="brand-logo-preview" />}
                                <input
                                    type="file"
                                    onChange={handleEditFileChange}
                                />
                                {editingBrand.logoUrl && (
                                    <div className="remove-logo-container">
                                         <input
                                            type="checkbox"
                                            id="removeLogoCheck"
                                            checked={removeLogo}
                                            onChange={(e) => setRemoveLogo(e.target.checked)}
                                        />
                                        <label htmlFor="removeLogoCheck">Remove current logo</label>
                                    </div>
                                )}
                            </div>
                             {error && <p className="brand-error">{error}</p>}

                            <div className="modal-actions">
                                <button type="submit" className="btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button type="button" className="btn-secondary" onClick={closeEditModal}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandManager;